import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowsRotate,
    faFileInvoiceDollar,
    faFolderOpen,
    faNoteSticky,
    faPaperPlane,
    faPlus,
    faShareNodes,
    faTrash,
    faUpload,
} from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import { ProjectOverviewItem } from 'apiClient/Api';
import { LinksDialog } from './LinksDialog';
import { OffersDialog } from './OffersDialog';
import { ShareProjectDialog } from './ShareProjectDialog';
import { CreatedShareLink, SendInvitationDialog, SendInvitationValues } from './SendInvitationDialog';

const money = (v?: number | null) =>
    v == null
        ? null
        : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const dateTime = (v?: string | null) => (v ? new Date(v).toLocaleString('de-DE') : '');

type Ref = { id: string; name: string } | null;

/**
 * Start-/Übersichtsseite des Kalkulators für OFFICE+ bzw. Träger von `project.link.manage`:
 * zeigt alle Kalkulationen mit Link-/Angebots-Status und erlaubt direktes Versenden, Angebote,
 * Freigeben und Öffnen. Eine neue eigene Konfiguration wird nur bei Bedarf gestartet.
 */
export const ProjectOverview: React.FC<{
    onOpen: (id: string) => void;
    onNew: () => void;
    onImport: (file: File) => void;
}> = ({ onOpen, onNew, onImport }) => {
    const { t } = useTranslation();
    const { api, apiError, hasPermission } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const importInput = React.useRef<HTMLInputElement>(null);
    const [items, setItems] = useState<ProjectOverviewItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [linksFor, setLinksFor] = useState<Ref>(null);
    const [offersFor, setOffersFor] = useState<Ref>(null);
    const [shareFor, setShareFor] = useState<Ref>(null);
    const [sendOpen, setSendOpen] = useState(false);

    const canLinks = hasPermission('project.link.manage');
    const canOffers = hasPermission('project.offer.upload');
    const canShare = hasPermission('project.share');

    const reload = useCallback(() => {
        setLoading(true);
        api.calculator
            .projectOverviewGetOverview()
            .then((r) => setItems(r.data ?? []))
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => reload(), [reload]);

    const remove = async (id: string, name: string) => {
        if (!window.confirm(t('calculation.store.deleteConfirm', { name }))) return;
        try {
            await api.calculator.projectsDelete(id);
            enqueueSnackbar(t('calculation.store.deleted'), { variant: 'success' });
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    // Direkt aus der Übersicht: neues (leeres) Projekt + Link + E-Mail in einem Schritt.
    const handleSendShared = async (values: SendInvitationValues): Promise<CreatedShareLink | null> => {
        try {
            const created = (
                await api.calculator.sharedProjectCreateShared({
                    customerEmail: values.customerEmail,
                    mode: values.mode,
                    projectName: values.name || null,
                    customerName: values.customerName || null,
                    customerReference: values.customerReference || null,
                    expiresAt: values.expiresAt,
                })
            ).data as unknown as CreatedShareLink;
            enqueueSnackbar(
                created.emailSent
                    ? t('calculation.invite.sentToast', 'Einladung gesendet.')
                    : t('calculation.invite.sentNoMailToast', 'Link erstellt – E-Mail-Versand fehlgeschlagen.'),
                { variant: created.emailSent ? 'success' : 'warning' },
            );
            reload();
            return created;
        } catch (e) {
            apiError(e);
            return null;
        }
    };

    const sorted = useMemo(
        () => [...items].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')),
        [items],
    );

    // Kompakte Status-Chips (nur relevante Zähler anzeigen).
    const linkChips = (p: ProjectOverviewItem) => {
        const l = p.links;
        if (!l || !l.total) return <Typography variant="caption" color="text.secondary">–</Typography>;
        return (
            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {!!l.opened && (
                    <Chip size="small" color="success" label={t('calculation.overview.opened', { count: l.opened })} />
                )}
                {!!l.sent && <Chip size="small" label={t('calculation.overview.sent', { count: l.sent })} />}
                {!!l.created && (
                    <Chip size="small" color="warning" variant="outlined" label={t('calculation.overview.created', { count: l.created })} />
                )}
                {!!l.revoked && (
                    <Chip size="small" variant="outlined" label={t('calculation.overview.revoked', { count: l.revoked })} />
                )}
                {!!l.expired && (
                    <Chip size="small" variant="outlined" label={t('calculation.overview.expired', { count: l.expired })} />
                )}
            </Stack>
        );
    };

    const offerChips = (p: ProjectOverviewItem) => {
        const o = p.offers;
        if (!o || !o.total) return <Typography variant="caption" color="text.secondary">–</Typography>;
        const approved = o.approved || 0;
        const notSatisfied = o.notSatisfied || 0;
        const callback = o.callbackRequested || 0;
        // Freigegeben, aber noch ohne Rückmeldung – „in Ordnung" u. a. gewinnen, keine Dopplung.
        const releasedPending = Math.max(0, (o.released || 0) - approved - notSatisfied - callback);
        const draft = Math.max(0, o.total - (o.released || 0));
        // Geplante Umsetzung des zuletzt angenommenen Angebots (aus der OfferSummary).
        const plannedLabel =
            o.plannedMonth && o.plannedYear
                ? `${String(o.plannedMonth).padStart(2, '0')}/${o.plannedYear}`
                : null;
        return (
            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {approved > 0 && (
                    <Chip
                        size="small"
                        color="success"
                        label={
                            plannedLabel
                                ? t('calculation.overview.approvedPlanned', { count: approved, date: plannedLabel })
                                : t('calculation.overview.approved', { count: approved })
                        }
                    />
                )}
                {notSatisfied > 0 && (
                    <Chip size="small" color="warning" label={t('calculation.overview.notSatisfied', { count: notSatisfied })} />
                )}
                {callback > 0 && (
                    <Chip size="small" color="info" label={t('calculation.overview.callback', { count: callback })} />
                )}
                {releasedPending > 0 && (
                    <Tooltip
                        title={o.downloaded ? t('calculation.overview.downloaded', { count: o.downloaded }) : ''}
                        disableHoverListener={!o.downloaded}
                    >
                        <Chip size="small" color="primary" label={t('calculation.overview.released', { count: releasedPending })} />
                    </Tooltip>
                )}
                {draft > 0 && (
                    <Chip size="small" variant="outlined" label={t('calculation.overview.draft', { count: draft })} />
                )}
            </Stack>
        );
    };

    return (
        <>
            <Paper sx={{ p: 2 }}>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
                    useFlexGap
                >
                    <Typography variant="h6">{t('calculation.overview.title', 'Kalkulationen')}</Typography>
                    <Stack direction="row" spacing={1}>
                        {canLinks && (
                            <Button
                                variant="contained"
                                startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                                onClick={() => setSendOpen(true)}
                            >
                                {t('calculation.invite.title', 'Neue Einladung versenden')}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={onNew}
                        >
                            {t('calculation.overview.new', 'Neue Konfiguration')}
                        </Button>
                        <Tooltip title={t('calculation.import', 'Import (JSON)')}>
                            <IconButton onClick={() => importInput.current?.click()}>
                                <FontAwesomeIcon icon={faUpload} />
                            </IconButton>
                        </Tooltip>
                        <input
                            ref={importInput}
                            type="file"
                            accept="application/json,.json"
                            hidden
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) onImport(f);
                                e.target.value = '';
                            }}
                        />
                        <Tooltip title={t('calculation.overview.refresh', 'Aktualisieren')}>
                            <IconButton onClick={reload}>
                                <FontAwesomeIcon icon={faArrowsRotate} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {!loading && sorted.length === 0 && (
                    <Typography color="text.secondary">{t('calculation.overview.empty', 'Noch keine Kalkulationen. Starten Sie eine neue Konfiguration.')}</Typography>
                )}

                {sorted.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('calculation.store.name')}</TableCell>
                                <TableCell>{t('calculation.overview.links', 'Einladungen')}</TableCell>
                                <TableCell>{t('calculation.overview.offers', 'Angebote')}</TableCell>
                                <TableCell align="right">{t('calculation.store.lastCalc')}</TableCell>
                                <TableCell>{t('calculation.store.updated')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((p) => (
                                <TableRow key={p.id} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {p.name}
                                            </Typography>
                                            {p.hasCustomerNotes && (
                                                <Tooltip title={t('calculation.overview.hasNotes', 'Kundennotizen vorhanden')}>
                                                    <Box component="span" sx={{ color: 'text.secondary' }}>
                                                        <FontAwesomeIcon icon={faNoteSticky} size="sm" />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                        {p.customerName && (
                                            <Typography variant="caption" color="text.secondary">
                                                {p.customerName}
                                                {p.customerReference ? ` · ${p.customerReference}` : ''}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{linkChips(p)}</TableCell>
                                    <TableCell>{offerChips(p)}</TableCell>
                                    <TableCell align="right">
                                        {money(p.lastCalcTotalNet) ? (
                                            <Typography variant="body2">{money(p.lastCalcTotalNet)}</Typography>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">–</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{dateTime(p.updatedAt)}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {p.updatedBy}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<FontAwesomeIcon icon={faFolderOpen} />}
                                            onClick={() => p.id && onOpen(p.id)}
                                        >
                                            {t('calculation.store.open')}
                                        </Button>
                                        {canLinks && (
                                            <Tooltip title={t('calculation.overview.sendManage', 'Versenden / Einladungen')}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => p.id && setLinksFor({ id: p.id, name: p.name || '' })}
                                                >
                                                    <FontAwesomeIcon icon={faPaperPlane} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canOffers && (
                                            <Tooltip title={t('calculation.overview.offers', 'Angebote')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => p.id && setOffersFor({ id: p.id, name: p.name || '' })}
                                                >
                                                    <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canShare && (
                                            <Tooltip title={t('calculation.store.share', 'Teilen')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => p.id && setShareFor({ id: p.id, name: p.name || '' })}
                                                >
                                                    <FontAwesomeIcon icon={faShareNodes} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={t('calculation.store.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => p.id && remove(p.id, p.name || '')}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <LinksDialog
                projectId={linksFor?.id ?? null}
                projectName={linksFor?.name}
                onClose={() => {
                    setLinksFor(null);
                    reload();
                }}
            />
            <OffersDialog
                projectId={offersFor?.id ?? null}
                projectName={offersFor?.name}
                onClose={() => {
                    setOffersFor(null);
                    reload();
                }}
            />
            <ShareProjectDialog
                projectId={shareFor?.id ?? null}
                projectName={shareFor?.name}
                onClose={() => setShareFor(null)}
            />
            <SendInvitationDialog
                open={sendOpen}
                hint={t(
                    'calculation.invite.hintNew',
                    'Es wird eine neue Konfiguration angelegt und dem Kunden per E-Mail als Link zugeschickt – der Kunde konfiguriert dann selbst.',
                )}
                onSend={handleSendShared}
                onClose={() => setSendOpen(false)}
            />
        </>
    );
};

export default ProjectOverview;
